import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@workos-inc/authkit-react';
import axios, { companyReportsAxiosInstance } from 'src/services/axiosInstance.service';
import * as authService from 'src/services/auth.service';
import * as errorsHandler from 'src/services/errors.service';

export const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading, getAccessToken, signIn } = useAuth();
  const [ready, setReady] = useState(false);
  // state:
  //   An optional parameter that can be used to encode arbitrary information to help restore application state between redirects. 
  //   If included, the redirect URI received from WorkOS will contain the exact state value that was passed.
  // Additionally, it seems to add quotes to the value if it's a string
  // And JSON strigify it if it is an object
  const relativePath = window.location.pathname + window.location.search + window.location.hash;
  const state = useMemo(() => { 
    return { path: relativePath };
  }, [relativePath]);

  useEffect(() => {
    if (isLoading || !user) {
      setReady(false);
      return;
    }

    const requestInterceptor = companyReportsAxiosInstance.interceptors.request.use(
      async (config) => {
        const token = await getAccessToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const axiosRequestInterceptor = axios.interceptors.request.use(
      async (config) => {
        const token = await getAccessToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const axiosResponseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          authService.deletePersistedCurrentUser();

          // This should be rare, but nice to handle
          if (error.response?.data?.error?.message === 'User currently not active in the system') {
            return Promise.reject({
              handled: true,
              message: error.response?.data?.error?.message,
              original: error,
            });
          }

          signIn({ state });
        }
        return errorsHandler.handleGlobalErrors(error, {
          authenticationErrorHandler: () => {},
        });
      }
    );

    // Set ready AFTER interceptors are configured
    setReady(true);

    return () => {
      companyReportsAxiosInstance.interceptors.request.eject(requestInterceptor);
      axios.interceptors.request.eject(axiosRequestInterceptor);
      axios.interceptors.response.eject(axiosResponseInterceptor);
      setReady(false);
    };
  }, [state, user, isLoading, getAccessToken, signIn]);

  // Order matters here!
  if (isLoading) return null;
  if (!user) { signIn({ state }); return null; }
  if (!ready) return null;

  return <>{children}</>;
};
